import { Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { Grid2 } from "@mui/material";
import TextField from "@mui/material/TextField";
import useStyles from "components/settings/user/form/styles";
import { ACTION_STATES } from "core/actions";
import { useFormik } from "formik";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { actions } from "./logic/actions";

export const passwordMatch = Yup.string()
    .when("newPassword", {
        is: (val) => !!val,
        then: () => Yup.string().oneOf([Yup.ref("newPassword"), null], "Passwords must match"),
        otherwise: ()=> Yup.string().nullable(),
    })
    .when("oldPassword", {
        is: (val) => !!val,
        then: () => Yup.string().required("Required"),
        otherwise: () => Yup.string().nullable(),
    });

export const passwordRuleSchema = Yup.string().matches(
    /^.*(?=.{8,})((?=.*[!@#$%^&*()\-_=+{};:,<.>]){1})(?=.*\d)((?=.*[a-z]){1})((?=.*[A-Z]){1}).*$/,
    "Password must contain at least 8 characters, one uppercase, one number and one special case character"
);

export const requiredSchema = Yup.string().nullable().required("Required");

export const validationSchemaPasswordChange = Yup.object().shape({
    oldPassword: Yup.string().nullable().required("Required"),
    newPassword: Yup.string().nullable().concat(requiredSchema).concat(passwordRuleSchema),
    newPasswordConfirmation: Yup.string().nullable().required("Required").concat(passwordMatch),
});

export function PasswordFormModal({ onClose, id }) {
    function handleClose() {
        onClose();
    }

    return (
        <Dialog
            open
            onClose={handleClose}
            aria-labelledby="alert-dialog-slide-title"
            aria-describedby="alert-dialog-slide-description"
        >
            <DialogTitle id="alert-dialog-slide-title">Change password</DialogTitle>
            <DialogContent>
                <DialogContentText id="alert-dialog-slide-description">
                    <FullPasswordForm onSuccess={handleClose} id={id} onClose={handleClose} />
                </DialogContentText>
            </DialogContent>
        </Dialog>
    );
}

PasswordFormModal.propTypes = {
    onClose: PropTypes.func,
    id: PropTypes.string,
};
export function usePasswordTypeChange() {
    const [showPassword, setShowPassword] = useState(false);
    const type = showPassword ? "text" : "password";
    function toggleShowPassword() {
        setShowPassword(!showPassword);
    }
    return [type, showPassword, toggleShowPassword];
}
export function FullPasswordForm({ onClose, id }) {
    const classes = useStyles();
    const [type, showPassword, toggleShowPassword] = usePasswordTypeChange();
    const [sending, setSending] = useState(false);
    const changingPasswordStatus = useSelector((state) => state.userForm.changePasswordStatus);
    const dispatch = useDispatch();

    const formik = useFormik({
        initialValues: {
            id,
            oldPassword: null,
            newPassword: null,
            newPasswordConfirmation: null,
        },
        validationSchema: validationSchemaPasswordChange,
        onSubmit: handleSubmit,
    });

    useEffect(() => {
        if (sending) {
            if (changingPasswordStatus === ACTION_STATES.SUCCESS) {
                onClose();
            }
            if (changingPasswordStatus === ACTION_STATES.FAILED) {
                setSending(false);
            }
        }
    }, [sending, changingPasswordStatus, onClose]);

    function handleSubmit() {
        if (formik.isValid) {
            setSending(true);
            const payload = { id, ...formik.values };
            dispatch(actions.CHANGE_PASSWORD.send(payload));
        }
    }

    function handleCancel() {
        onClose();
    }

    return (
        (<form>
            <Grid2 container spacing={3}>
                <FullPasswordFields formik={formik} type={type} />
                <Grid2 size={12}>
                    <Button
                        variant={"text"}
                        type={"button"}
                        size={"small"}
                        color={"secondary"}
                        onClick={toggleShowPassword}
                    >
                        {showPassword ? "Hide passwords" : "Show passwords"}
                    </Button>
                </Grid2>
                <Grid2 size={12}>
                    <Grid2 size={12}>
                        <Box className={classes.formButtonsContainer}>
                            <Button variant={"outlined"} type={"button"} onClick={handleCancel}>
                                Cancel
                            </Button>
                            <Button
                                variant={"contained"}
                                type={"button"}
                                disabled={sending}
                                onClick={formik.handleSubmit}
                                color={"primary"}
                            >
                                Save
                            </Button>
                        </Box>
                    </Grid2>
                </Grid2>
            </Grid2>
        </form>)
    );
}
FullPasswordForm.propTypes = {
    onClose: PropTypes.func,
    id: PropTypes.string,
};

export function FullPasswordFields({ formik, type }) {
    return (<>
        <Grid2 size={12}>
            <Typography variant={"subtitle2"} className={"headerForm"}>
                Old Password
            </Typography>
            <TextField
                className="form-control"
                margin="normal"
                name={"oldPassword"}
                type={type}
                autoComplete={"off"}
                onChange={formik.handleChange}
                value={formik.values.oldPassword}
                onBlur={formik.handleBlur}
                helperText={formik.touched.oldPassword ? formik.errors.oldPassword : ""}
                error={formik.touched.oldPassword && Boolean(formik.errors.oldPassword)}
                variant="outlined"
                slotProps={{
                    htmlInput: {
                        autoComplete: "off",
                    }
                }}
            />
        </Grid2>
        <PasswordChangeForm formik={formik} type={type} />
    </>);
}

FullPasswordFields.propTypes = {
    formik: PropTypes.object,
    type: PropTypes.string,
};

export function PasswordChangeForm({ formik, type, isNew }) {
    const { touched, handleBlur, errors, handleChange } = formik;
    const passLabel = isNew ? "Password" : "New Password";
    return (
        (<Grid2 size={12}>
            <Box>
                <Grid2 container spacing={3}>
                    <Grid2 size={12}>
                        <Typography variant={"subtitle2"} className={"headerForm"}>
                            {passLabel}
                        </Typography>
                        <TextField
                            className="form-control"
                            margin="normal"
                            type={type}
                            name={"newPassword"}
                            onBlur={handleBlur}
                            autoComplete="new-password"
                            onChange={handleChange}
                            helperText={touched.newPassword ? errors.newPassword : ""}
                            error={touched.newPassword && Boolean(errors.newPassword)}
                            value={formik.values.newPassword}
                            variant="outlined"
                            slotProps={{
                                htmlInput: {
                                    autoComplete: "new-password",
                                }
                            }}
                        />
                    </Grid2>
                    <Grid2 size={12}>
                        <Typography variant={"subtitle2"} className={"headerForm"}>
                            Confirm Password
                        </Typography>
                        <TextField
                            className="form-control"
                            margin="normal"
                            type={type}
                            name={"newPasswordConfirmation"}
                            value={formik.values.newPasswordConfirmation}
                            autoComplete="new-password"
                            onBlur={handleBlur}
                            onChange={handleChange}
                            helperText={touched.newPasswordConfirmation ? errors.newPasswordConfirmation : ""}
                            error={touched.newPasswordConfirmation && Boolean(errors.newPasswordConfirmation)}
                            variant="outlined"
                            slotProps={{
                                htmlInput: {
                                    autoComplete: "new-password",
                                }
                            }}
                        />
                    </Grid2>
                </Grid2>
            </Box>
        </Grid2>)
    );
}

PasswordChangeForm.propTypes = {
    formik: PropTypes.object,
    type: PropTypes.string,
    isNew: PropTypes.bool,
};
